import i18n from "../../../utils/i18n";
import CommonCegjelzoAuto from "../steps/common/CommonCegjelzoAuto.jsx";
import CommonCompanyType from "../steps/common/CommonCompanyType.jsx";
import CommonKftCegjelzoInterstitial from "../steps/common/CommonKftCegjelzoInterstitial.jsx";
import CommonKftInterstitial from "../steps/common/CommonKftInterstitial.jsx";
import CommonValueProp from "../steps/common/CommonValueProp.jsx";
import NewMultiCompanyInterstitial from "../steps/common/NewMultiCompanyInterstitial.jsx";
import { isCegjelzoRegistryTypeAutoFillSupported } from "./cegjelzo/mapCegjelzoCompanyData.js";
import CommonFillingMethod from "../steps/common/CommonFillingMethod.jsx";
import CommonMoreBranch from "../steps/common/CommonMoreBranch.jsx";
import CommonRouting from "../steps/common/CommonRouting.jsx";
import CompanyIndustrySelect from "../steps/company/CompanyIndustrySelect.jsx";
import CompanyQuickResult from "../steps/company/CompanyQuickResult.jsx";
import CompanyEmployeeNo, {
  isCompanyEmployeeNoValid,
} from "../steps/company/CompanyEmployeeNo.jsx";
import CompanyForeignTrade, {
  isCompanyForeignTradeAnswered,
} from "../steps/company/CompanyForeignTrade.jsx";
import CompanyIndustryActivity from "../steps/company/CompanyIndustryActivity.jsx";
import CompanyRevenue, {
  isCompanyRevenueAnswered,
} from "../steps/company/CompanyRevenue.jsx";
import CompanyCegjelzoTaxForm from "../steps/company/CompanyCegjelzoTaxForm.jsx";
import CompanyTaxForm, { isCompanyTaxForm } from "../steps/company/CompanyTaxForm.jsx";
import EvCegjelzoTaxForm from "../steps/ev/EvCegjelzoTaxForm.jsx";
import EvIndustrySelect from "../steps/ev/EvIndustrySelect.jsx";
import EvQuickResult from "../steps/ev/EvQuickResult.jsx";
import EvForeignTradeStep, {
  isEvForeignTradeAnswered,
} from "../steps/ev/EvForeignTrade.jsx";
import EvIndustryActivity from "../steps/ev/EvIndustryActivity.jsx";
import EvRevenue, { isEvRevenueAnswered } from "../steps/ev/EvRevenue.jsx";
import { isEntrepreneurTaxCompanyType } from "../steps/ev/EvTaxForm.jsx";
import EvTaxForm from "../steps/ev/EvTaxForm.jsx";
import EvVatStatus, { isEvVatFilterAnswered } from "../steps/ev/EvVatStatus.jsx";
import CompanyVatStatus, {
  isCompanyVatFilterAnswered,
} from "../steps/company/CompanyVatStatus.jsx";
import NewCompanyType from "../steps/other/NewCompanyType.jsx";

const COMPANY_TYPE_OTHER_LEAVES = new Set([
  "bt",
  "nyrt",
  "civil",
  "kkt",
  "zrt",
  "condo",
  "citycouncil",
]);

const withQuickResultPrefetchFooter = (self, nav, showsActivityStep) => {
  if (showsActivityStep() || !self.state.isQuickResultPrefetching) {
    return nav;
  }

  return {
    ...nav,
    nextDisabled: true,
    nextLoading: true,
  };
};

const withIndustryActivityPrefetchFooter = (self, nav) => {
  if (!self.state.isQuickResultPrefetching) {
    return nav;
  }

  return {
    ...nav,
    nextDisabled: true,
    nextLoading: true,
  };
};

const guardNextStep = (onNext, nextDisabled) => () => {
  if (nextDisabled) {
    return;
  }
  onNext?.();
};

const getIndustrySelectFooterNav = (self, onBack, onNext) => {
  const selectedIndustry = self.props.company_store?.industry || "";
  const industryInput = self.props.company_store?.industryinput || "";
  const isOtherSelected = selectedIndustry === "other";
  const nextDisabled =
    !selectedIndustry || (isOtherSelected && !String(industryInput).trim());

  return {
    onBack,
    onNext: guardNextStep(onNext, nextDisabled),
    nextDisabled,
  };
};

/** Footer config for the active wireframe step — rendered outside step transitions. */
export const getStepFooterNav = (self, activeStep, steps) => {
  const stepComponent = steps[activeStep]?.component;
  if (!stepComponent) {
    return null;
  }

  if (stepComponent === CommonValueProp) {
    return null;
  }

  if (stepComponent === CommonRouting) {
    const canContinue =
      self.state.routingSelection === "existing" ||
      self.state.routingSelection === "founding";
    const nextDisabled = !canContinue;
    return {
      onBack: self.handleRoutingBack,
      onNext: guardNextStep(self.handleRoutingNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === NewCompanyType) {
    const nextDisabled = !self.state.regCompanyTypeSelection;
    return {
      onBack: self.handleNewCompanyTypeBack,
      onNext: guardNextStep(self.handleNewCompanyTypeNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === CommonMoreBranch) {
    const canContinue =
      self.state.moreBranchSelection === "singlecompany" ||
      self.state.moreBranchSelection === "multiplecompany";
    const nextDisabled = !canContinue;
    return {
      onBack: self.handleMoreBranchBack,
      onNext: guardNextStep(self.handleMoreBranchNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === NewMultiCompanyInterstitial) {
    const selection = self.state.multiCompanyInterstitialSelection;
    const nextDisabled = !selection;
    return {
      onBack: self.handleMultiCompanyInterstitialBack,
      onNext: guardNextStep(self.handleMultiCompanyInterstitialNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === CommonFillingMethod) {
    return {
      onBack: self.handleFillingMethodBack,
      onNext: self.handleFillingMethodNext,
      nextDisabled: false,
    };
  }

  if (stepComponent === CommonCegjelzoAuto) {
    const selectedCompany = self.props.company_store?.cegjelzoData;
    const isSupported =
      selectedCompany &&
      isCegjelzoRegistryTypeAutoFillSupported(selectedCompany.type);
    const nextDisabled =
      !selectedCompany || !isSupported || self.state.cegjelzoPersisting;
    return {
      onBack: self.handleCegjelzoAutoBack,
      onNext: guardNextStep(self.handleCegjelzoAutoNext, nextDisabled),
      nextDisabled,
      nextLabel: self.state.cegjelzoPersisting
        ? i18n.t("searchflow.savingdots")
        : i18n.t("general.forwardbutton"),
    };
  }

  if (stepComponent === CommonCompanyType) {
    const selection = self.state.companyTypeFlowSelection;
    const nextDisabled = !selection || selection === "other";
    const onNext = () => {
      if (nextDisabled) {
        return;
      }
      if (selection === "kft") {
        self.handleCompanyTypeKftNext();
        return;
      }
      if (COMPANY_TYPE_OTHER_LEAVES.has(selection)) {
        self.handleCompanyTypeCompanyNext();
        return;
      }
      self.handleCompanyTypeSoleProprietorshipNext();
    };
    return {
      onBack: self.handleCompanyTypeBack,
      onNext,
      nextDisabled,
    };
  }

  if (stepComponent === CommonKftInterstitial) {
    const nextDisabled = !self.state.kftInterstitialSelection;
    return {
      onBack: self.handleKftInterstitialBack,
      onNext: guardNextStep(self.handleKftInterstitialNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === CommonKftCegjelzoInterstitial) {
    const nextDisabled = !self.state.kftCegjelzoInterstitialSelection;
    return {
      onBack: self.handleKftCegjelzoInterstitialBack,
      onNext: guardNextStep(self.handleKftCegjelzoInterstitialNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === EvIndustrySelect) {
    return withQuickResultPrefetchFooter(
      self,
      getIndustrySelectFooterNav(
        self,
        self.handleEvIndustrySelectBack,
        self.handleIndustrySelectNext,
      ),
      self.evIndustryShowsActivityStep,
    );
  }

  if (stepComponent === CompanyIndustrySelect) {
    return withQuickResultPrefetchFooter(
      self,
      getIndustrySelectFooterNav(
        self,
        self.handleCompanyIndustrySelectBack,
        self.handleCompanyIndustrySelectNext,
      ),
      self.companyIndustryShowsActivityStep,
    );
  }

  if (stepComponent === EvQuickResult) {
    return {
      onBack: self.handleEvQuickResultBack,
      onNext: self.handleEvQuickResultNext,
      nextVariant: "continue",
      nextLabel: i18n.t("searchflow.continuefilling"),
    };
  }

  if (stepComponent === CompanyQuickResult) {
    return {
      onBack: self.handleCompanyQuickResultBack,
      onNext: self.handleCompanyQuickResultNext,
      nextVariant: "continue",
      nextLabel: i18n.t("searchflow.continuefilling"),
    };
  }

  if (stepComponent === EvTaxForm) {
    const companytype = self.props.company_store?.companytype;
    // Frontend-only guard: require an explicit pick on this screen, even if companytype
    // already has a stored default (e.g. the EV flow's átalányadó default).
    const nextDisabled =
      !self.state.evTaxFormInteracted || !isEntrepreneurTaxCompanyType(companytype);
    return {
      onBack: self.handleTaxFormBack,
      onNext: guardNextStep(self.handleTaxFormNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === EvCegjelzoTaxForm) {
    const companytype = self.props.company_store?.companytype;
    // Frontend-only guard: require an explicit pick on this screen, even if companytype
    // already has a stored default (e.g. the Cégjelző self-employed → átalányadó default).
    // Shares the flag with EvTaxForm since the manual and auto-fill branches are mutually exclusive.
    const nextDisabled =
      !self.state.evTaxFormInteracted || !isEntrepreneurTaxCompanyType(companytype);
    return {
      onBack: self.handleEvCegjelzoTaxFormBack,
      onNext: guardNextStep(self.handleEvCegjelzoTaxFormNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === EvVatStatus) {
    const vatFilter = self.props.company_store?.vatFilter;
    const nextDisabled = !isEvVatFilterAnswered(vatFilter);
    return {
      onBack: self.handleEvVatStatusBack,
      onNext: guardNextStep(self.handleEvVatStatusNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === EvRevenue) {
    const yearlyrevenue = self.props.company_store?.yearlyrevenue;
    const nextDisabled = !isEvRevenueAnswered(yearlyrevenue);
    return {
      onBack: self.handleEvRevenueBack,
      onNext: guardNextStep(self.handleEvRevenueNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === CompanyCegjelzoTaxForm) {
    const taxform = self.props.company_store?.taxform;
    // Frontend-only guard: require an explicit pick on this screen, even if taxform
    // already has a stored default. Shares the flag with CompanyTaxForm since the
    // manual and auto-fill branches are mutually exclusive.
    const nextDisabled =
      !self.state.companyTaxFormInteracted || !isCompanyTaxForm(taxform);
    return {
      onBack: self.handleCompanyCegjelzoTaxFormBack,
      onNext: guardNextStep(self.handleCompanyCegjelzoTaxFormNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === CompanyTaxForm) {
    const taxform = self.props.company_store?.taxform;
    // Frontend-only guard: require an explicit pick on this screen, even if taxform
    // already has a stored default.
    const nextDisabled =
      !self.state.companyTaxFormInteracted || !isCompanyTaxForm(taxform);
    return {
      onBack: self.handleCompanyTaxFormBack,
      onNext: guardNextStep(self.handleCompanyTaxFormNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === CompanyRevenue) {
    const yearlyrevenue = self.props.company_store?.yearlyrevenue;
    const nextDisabled = !isCompanyRevenueAnswered(yearlyrevenue);
    return {
      onBack: self.handleCompanyRevenueBack,
      onNext: guardNextStep(self.handleCompanyRevenueNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === CompanyEmployeeNo) {
    const noofemployees = self.props.company_store?.noofemployees;
    const nextDisabled = !isCompanyEmployeeNoValid(noofemployees);
    return {
      onBack: self.handleCompanyEmployeeNoBack,
      onNext: guardNextStep(self.handleCompanyEmployeeNoNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === CompanyVatStatus) {
    const vatFilter = self.props.company_store?.vatFilter;
    const nextDisabled = !isCompanyVatFilterAnswered(vatFilter);
    return {
      onBack: self.handleCompanyVatStatusBack,
      onNext: guardNextStep(self.handleCompanyVatStatusNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === EvForeignTradeStep) {
    const { foreign_trade, foreigntradeEU } = self.props.company_store || {};
    const nextDisabled = !isEvForeignTradeAnswered(foreign_trade, foreigntradeEU);
    return {
      onBack: self.handleEvForeignTradeBack,
      onNext: guardNextStep(self.handleEvForeignTradeNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === EvIndustryActivity) {
    return withIndustryActivityPrefetchFooter(self, {
      onBack: self.handleEvIndustryActivityBack,
      onNext: self.handleEvIndustryActivityNext,
      nextDisabled: false,
    });
  }

  if (stepComponent === CompanyForeignTrade) {
    const { foreign_trade, foreigntradeEU } = self.props.company_store || {};
    const nextDisabled = !isCompanyForeignTradeAnswered(foreign_trade, foreigntradeEU);
    return {
      onBack: self.handleCompanyForeignTradeBack,
      onNext: guardNextStep(self.handleCompanyForeignTradeNext, nextDisabled),
      nextDisabled,
    };
  }

  if (stepComponent === CompanyIndustryActivity) {
    return withIndustryActivityPrefetchFooter(self, {
      onBack: self.handleCompanyIndustryActivityBack,
      onNext: self.handleCompanyIndustryActivityNext,
      nextDisabled: false,
    });
  }

  return null;
};
